import { motion, useReducedMotion } from "motion/react";

import { toggleTheme, useTheme } from "../lib/theme";

/**
 * Light/dark switch.
 *
 * The accessibility of a theme toggle is almost always got wrong in the same
 * way: an icon-only button with no name, or a name that describes the current
 * state rather than the action. This one is a plain `<button>` whose
 * accessible name says what pressing it will *do* — "Switch to dark theme" —
 * because that is the question a screen-reader user is answering.
 *
 * Not `role="switch"`: that would announce "on/off", and neither theme is
 * off. It is a button that changes an appearance.
 *
 * The icon crossfades and rotates rather than swapping, so the control reads
 * as one object changing state instead of two icons trading places.
 */

const SUN =
  "M12 4V2m0 20v-2m8-8h2M2 12h2m13.66-5.66 1.42-1.42M4.92 19.08l1.42-1.42m11.32 0 1.42 1.42M4.92 4.92l1.42 1.42";

export function ThemeToggle() {
  const theme = useTheme();
  const reduced = useReducedMotion() ?? false;
  const next = theme === "dark" ? "light" : "dark";

  const spin = reduced
    ? { duration: 0 }
    : ({ type: "spring", stiffness: 200, damping: 20 } as const);

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="group relative -m-2 inline-flex size-9 items-center justify-center rounded-full p-2 text-text-dim transition-colors duration-300 hover:text-accent"
    >
      <span className="sr-only">Switch to {next} theme</span>
      <motion.svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className="size-[18px]"
        initial={false}
        animate={{ rotate: theme === "dark" ? -90 : 0 }}
        transition={spin}
      >
        {theme === "dark" ? (
          // Crescent: a filled circle with a second circle knocked out of it,
          // which is one path rather than a mask.
          <path
            d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z"
            fill="currentColor"
            stroke="none"
          />
        ) : (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d={SUN} />
          </>
        )}
      </motion.svg>
    </button>
  );
}
